import { useEffect } from 'react';

// Input types where Enter should move to the next field.
// Excludes: submit, button, reset, checkbox, radio, file, range, color.
const TEXT_INPUT_TYPES = new Set([
  'text', 'number', 'email', 'tel', 'password', 'search', 'url', 'date',
  'time', 'datetime-local', 'month', 'week', '',
]);

// Selector for elements that are reachable via Tab (and therefore next-field candidates).
const FOCUSABLE_SELECTOR = [
  'input:not([disabled]):not([readonly]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled]):not([readonly])',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function isVisible(el: HTMLElement): boolean {
  if (!el.offsetParent && (el as HTMLElement).style.position !== 'fixed') return false;
  const s = window.getComputedStyle(el);
  return s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0';
}

/**
 * Attaches a document-level keydown listener so pressing Enter on a text-like
 * input moves focus to the next focusable element — mirroring Tab behaviour.
 *
 * Safe to call once at the layout level; it covers the entire app.
 */
export function useEnterKeyNavigation(): void {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'Enter') return;

      const target = e.target as HTMLElement;
      const tag = target.tagName.toLowerCase();
      const type = ((target as HTMLInputElement).type ?? '').toLowerCase();
      const role = target.getAttribute('role') ?? '';

      // Textarea: Enter = newline — leave it alone.
      if (tag === 'textarea') return;

      // Button elements: Enter = click — leave alone.
      if (tag === 'button') return;

      // Non-text input types (checkbox, radio, submit, file, …): leave alone.
      if (tag === 'input' && !TEXT_INPUT_TYPES.has(type)) return;

      // AntD Select / DatePicker / AutoComplete all render an internal input
      // with role="combobox".  Their own keyboard handling must not be blocked.
      if (role === 'combobox') return;

      // If any ancestor has an open AntD dropdown (aria-expanded="true"), bail.
      if (target.closest('[aria-expanded="true"]')) return;

      // Collect all currently visible, focusable elements in DOM order.
      const focusable = Array.from(
        document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(isVisible);

      const idx = focusable.indexOf(target);
      if (idx >= 0 && idx < focusable.length - 1) {
        e.preventDefault();
        focusable[idx + 1].focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
}
